import { useState } from "react";
import { toast } from "sonner";
import { valorDaTaxaDeUrgencia } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { useConfirm } from "@/lib/confirm";
import { usePermissao } from "@/lib/use-permissao";
import { usePrazoDoLaboratorio } from "@/lib/prazo-do-laboratorio";
import { isUrgentDeadline } from "@/lib/prosthesis-urgency";
import { useTaxaDeUrgencia } from "@/lib/taxa-de-urgencia";
import { formatBRL } from "@/components/prosthesis/prosthesis-meta";
import type { IncomingOrderRow } from "./production-types";
import { useEtiquetasAoAceitar } from "./use-etiquetas-ao-aceitar";

/**
 * Aceitar o pedido — o mesmo caminho na lista de Pedidos e no detalhe. Pedido urgente, com taxa de
 * urgência configurada e quem aceita podendo mexer em preço: o diálogo traz "Cobrar taxa de
 * urgência" já marcado (desmarca pra dispensar) e a taxa entra como acréscimo junto com o aceite.
 */
export function useAceitarPedido() {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const prazo = usePrazoDoLaboratorio();
  const taxa = useTaxaDeUrgencia();
  const podePrecos = usePermissao("prices.manage");
  const etiquetasAoAceitar = useEtiquetasAoAceitar();
  const aceite = trpc.prosthesis.incoming.acceptOrder.useMutation({
    onSuccess: (r) => {
      toast.success(r.taxaCents ? `Pedido aceito, com taxa de urgência de ${formatBRL(r.taxaCents)}.` : "Pedido aceito.");
      utils.prosthesis.invalidate();
      if (r.taxaCents) utils.financeiro.invalidate();
    },
    onError: (e) => toast.error(e.message || "Erro ao aceitar pedido."),
  });

  async function aceitar(order: IncomingOrderRow) {
    const urgente = isUrgentDeadline(order.deadline, order.createdAt, prazo);
    const valor = taxa && urgente && podePrecos ? valorDaTaxaDeUrgencia(taxa, order.services ?? []) : 0;
    const escolha = { cobrar: valor > 0 };
    const texto = `O pedido de "${order.patientName}" entrará na fila de produção.`;
    const ok = await confirm({
      title: "Aceitar pedido?",
      description:
        valor > 0 ? (
          <>
            {texto}
            <CobrarTaxa valor={valor} onChange={(v) => (escolha.cobrar = v)} />
          </>
        ) : (
          texto
        ),
      confirmText: "Aceitar pedido",
      cancelText: "Voltar",
    });
    if (!ok) return;
    const etiquetas = etiquetasAoAceitar(order);
    aceite.mutate(
      { orderId: order.id, cobrarTaxaDeUrgencia: escolha.cobrar || undefined },
      { onSuccess: etiquetas.imprimir, onError: etiquetas.cancelar },
    );
  }

  return { aceitar, isPending: aceite.isPending };
}

function CobrarTaxa({ valor, onChange }: { valor: number; onChange: (v: boolean) => void }) {
  const [marcado, setMarcado] = useState(true);
  return (
    <label className="mt-2.5 flex cursor-pointer items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-[12px] text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
      <input
        type="checkbox"
        checked={marcado}
        onChange={(e) => {
          setMarcado(e.target.checked);
          onChange(e.target.checked);
        }}
        className="mt-0.5 h-3.5 w-3.5 accent-brand-600"
      />
      <span>
        Cobrar taxa de urgência ({formatBRL(valor)})
        <span className="block text-[11px] opacity-80">O prazo pedido é mais curto que o do laboratório. Desmarque para dispensar.</span>
      </span>
    </label>
  );
}
